import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Tabs from '@cloudscape-design/components/tabs';
import Button from '@cloudscape-design/components/button';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Box from '@cloudscape-design/components/box';
import Flashbar, { FlashbarProps } from '@cloudscape-design/components/flashbar';
import Modal from '@cloudscape-design/components/modal';
import Spinner from '@cloudscape-design/components/spinner';

import { describeStore, deleteStore } from '../api/stores';
import { getCredentials } from '../api/client';
import { getEvents } from '../api/events';
import { ConfigurationTab } from './tabs/ConfigurationTab';
import { FlowTab, SelectedEntity } from './tabs/FlowTab';
import { EventsTab } from './tabs/EventsTab';
import { DeadLetterTab } from './tabs/DeadLetterTab';
import { getStatusType } from '../utils/statusUtils';

interface StoreData {
  name: string;
  arn?: string;
  domain?: string;
  status?: string;
  createdAt?: string;
  streamKey?: {
    fields?: string[];
    delimiter?: string;
  };
  deletionProtection?: boolean;
  endpoints?: {
    events?: string;
    lineage?: string;
  };
}

export function StoreDetail() {
  const { storeName } = useParams<{ storeName: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);

  // Derive selected entity and active tab from URL path
  const selectedEntity = useMemo<SelectedEntity | null>(() => {
    const path = location.pathname;
    const storePrefix = `/stores/${storeName}`;
    const suffix = path.slice(storePrefix.length);

    const schemasMatch = suffix.match(/^\/schemas\/(.+)$/);
    if (schemasMatch) return { type: 'schema', id: decodeURIComponent(schemasMatch[1]) };

    const rulesMatch = suffix.match(/^\/rules\/(.+)$/);
    if (rulesMatch) return { type: 'rule', id: decodeURIComponent(rulesMatch[1]) };

    const eventsMatch = suffix.match(/^\/events\/(.+)$/);
    if (eventsMatch) return null; // events route switches to Ledger tab, no graph entity

    const resourcesMatch = suffix.match(/^\/resources\/(.+)$/);
    if (resourcesMatch) return { type: 'resource', id: decodeURIComponent(resourcesMatch[1]) };

    return null;
  }, [location.pathname, storeName]);

  // Tab state: default to 'graph', override to 'graph' when entity is in URL
  const [localTab, setLocalTab] = useState('graph');

  const activeTab = useMemo(() => {
    const path = location.pathname;
    const storePrefix = `/stores/${storeName}`;
    const suffix = path.slice(storePrefix.length);

    if (suffix.startsWith('/events/')) return 'events';
    if (selectedEntity) return 'graph';
    return localTab;
  }, [location.pathname, storeName, selectedEntity, localTab]);

  const { data, isLoading } = useQuery({
    queryKey: ['store', storeName],
    queryFn: () => describeStore(storeName!),
    enabled: !!getCredentials() && !!storeName,
  });

  // Prefetch events data for the Ledger tab (browse-all mode) so it's ready when
  // the user clicks Ledger. The query key matches EventsTab's default browse-all
  // state: ['events', storeName, submittedStream='', cursor=undefined, filter='', browseAll=true].
  useQuery({
    queryKey: ['events', storeName, '', undefined, '', true],
    queryFn: () => getEvents(storeName!, '', { limit: 25, sort: 'desc' }),
    enabled: !!getCredentials() && !!storeName,
    refetchInterval: 10000,
  });

  const store: StoreData = (data?.data as StoreData) || { name: storeName || '' };
  const resolvedStreamKey = store.streamKey;

  const displayStatus = store.status || 'UNKNOWN';
  const isActive = store.status === 'ACTIVE';
  const isDeleted = store.status === 'DELETED';

  const deleteMutation = useMutation({
    mutationFn: () => deleteStore(storeName!),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['stores'] });
      if (result.status === 404) {
        navigate('/stores');
      } else {
        navigate('/stores', { state: { flash: `Store '${storeName}' deleted.` } });
      }
    },
    onError: (err: Error) => {
      setShowDeleteModal(false);
      setFlash([{
        type: 'error',
        content: err.message,
        dismissible: true,
        onDismiss: () => setFlash([]),
      }]);
    },
  });

  if (isLoading) {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }

  return (
    <SpaceBetween size="l">
      <BreadcrumbGroup
        items={[
          { text: 'Event stores', href: '/stores' },
          { text: storeName || '', href: '' },
        ]}
        onFollow={(event) => {
          event.preventDefault();
          navigate(event.detail.href);
        }}
      />

      <Flashbar items={flash} />

      {isDeleted && (
        <Flashbar items={[{
          type: 'info',
          content: 'This store has been deleted. Configuration is read-only.',
          dismissible: false,
        }]} />
      )}

      <Header
        variant="h1"
        info={
          <StatusIndicator type={getStatusType(displayStatus)}>
            {displayStatus}
          </StatusIndicator>
        }
        actions={
          isActive ? (
            <Button
              variant="normal"
              disabled={store.deletionProtection}
              disabledReason="Disable deletion protection in the Configuration tab first"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete
            </Button>
          ) : undefined
        }
      >
        {storeName}
      </Header>

      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => {
          setLocalTab(detail.activeTabId);
          if (selectedEntity) {
            navigate(`/stores/${storeName}`, { replace: true });
          }
        }}
        tabs={[
          {
            id: 'graph',
            label: 'Graph',
            content: (
              <FlowTab
                storeName={storeName!}
                selectedEntity={selectedEntity}
                onEntitySelect={(entity) => {
                  if (entity) {
                    const segment = entity.type === 'schema' ? 'schemas' : 'rules';
                    navigate(`/stores/${storeName}/${segment}/${encodeURIComponent(entity.id)}`);
                  } else {
                    navigate(`/stores/${storeName}`, { replace: true });
                  }
                }}
              />
            ),
          },
          {
            id: 'events',
            label: 'Ledger',
            content: (
              <EventsTab
                storeName={storeName!}
                isActive={isActive}
                streamKey={resolvedStreamKey}
              />
            ),
          },
          {
            id: 'configuration',
            label: 'Settings',
            content: <ConfigurationTab store={store} />,
          },
          {
            id: 'dead-letter',
            label: 'Dead Letter',
            content: <DeadLetterTab storeName={storeName!} />,
          },
        ]}
      />

      {showDeleteModal && (
        <Modal
          visible={true}
          onDismiss={() => setShowDeleteModal(false)}
          header={`Delete event store '${storeName}'?`}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  loading={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate()}
                >
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          This action cannot be undone. The store will be marked as deleted.
        </Modal>
      )}
    </SpaceBetween>
  );
}
